import { useQuery } from "@tanstack/react-query";
import { ServiceIntegration } from "@/types/api-v2";
import { serviceIntegrationsApi } from "@/services/api-v2.service";

// Расширяем интерфейс для типизации настроек сервиса поиска
interface SearchServiceSettings {
  url: string;
  params: {
    format: string;
    limit: number;
    "accept-language": string;
    [key: string]: any;
  };
}

// Расширяем тип ServiceIntegration для типизации настроек
interface SearchServiceIntegration extends ServiceIntegration {
  settings: SearchServiceSettings;
}

export function useSearchServices() {
  const {
    data: activeServices = [],
    isLoading,
    error,
  } = useQuery<ServiceIntegration[]>({
    queryKey: ['service-integrations', 'active', 'search'],
    queryFn: async () => {
      try {
        // Используем параметр type через query string
        const integrations = await serviceIntegrationsApi.getActive('search');
        return integrations;
      } catch (err: any) {
        // Обрабатываем 502 и другие ошибки как пустой массив (будет использован дефолтный сервис)
        // 502 означает, что бэкенд временно недоступен, это не критично
        const status = err?.message?.match(/^(\d+):/)?.[1];
        if (status === '502' || status === '503' || status === '504') {
          console.debug('Search services temporarily unavailable, using default');
        } else {
          console.debug('Failed to fetch search services, using default:', err);
        }
        return [];
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // Кэшируем на 5 минут
  });

  // Получение активного сервиса поиска (первый активный, если их несколько)
  const activeSearchService = activeServices.find(service => 
    service.serviceType === 'search' && service.isActive
  ) as SearchServiceIntegration | undefined;

  // Используем дефолтный Nominatim, если нет настроенных сервисов
  const defaultSearchService: SearchServiceSettings = {
    url: "https://nominatim.openstreetmap.org",
    params: {
      format: "json",
      limit: 1,
      "accept-language": "ru"
    }
  };

  return {
    activeSearchService,
    searchSettings: activeSearchService?.settings || defaultSearchService,
    isLoading,
    error
  };
}